import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { useListEntities } from 'src/features/entity/api/useListEntities';
import CellEntityActions from 'src/features/entity/components/cell/CellEntityActions';
import CreateEntityGroupFormModal from 'src/features/entity/components/forms/CreateEntityGroupFormModal';
import { Entity, ENTITY_TYPE } from 'src/features/entity/types';
import { entityMessages } from 'src/features/entity/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellLink } from 'src/registry/components/table/cells/CellLink';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';

const columnHelper = createColumnHelper<Entity>();

const GroupsTable = () => {
    const { formatMessage } = useIntl();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'name', desc: false }],
    });
    const manageEntityPermission = useHasPermissions([PERMISSION.MANAGE_ENTITIES]);

    const { parkingId } = useParking();
    const {
        data: listEntities,
        isError,
        isLoading,
    } = useListEntities({
        parkingId,
        type: ENTITY_TYPE.ORGANISATION,
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...querySearchOptions,
    });

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            cell: (ctx) => {
                return (
                    <div className="flex flex-col">
                        <CellLink
                            to={`/${PATH.ENTITIES}/${ENTITY_TYPE.ORGANISATION}/${ctx.row.original.id}`}
                            {...ctx}
                        />
                        <div className="text-gray-600">{ctx.row.original.identifier}</div>
                    </div>
                );
            },
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(entityMessages.name),
            },
        }),
        columnHelper.accessor('phone', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(entityMessages.phone),
            },
        }),
        columnHelper.accessor('email', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(entityMessages.email),
            },
        }),
        columnHelper.accessor('description', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(entityMessages.description),
            },
        }),
        columnHelper.display({
            id: 'actions',
            cell: (ctx) => <CellEntityActions {...ctx} type={ENTITY_TYPE.ORGANISATION} />,
            maxSize: 45,
        }),
    ];

    const placeholder = `${formatMessage(entityMessages.name)}, ${formatMessage(entityMessages.phone)}, ${formatMessage(entityMessages.email)}`;

    return (
        <>
            <DataTable
                columns={columns as any}
                data={listEntities}
                pagination={pagination}
                sorting={sorting}
                search={search}
                semantics={{
                    title: formatMessage(entityMessages.groups),
                    entryName: formatMessage(entityMessages.group).toLowerCase(),
                    entryNames: formatMessage(entityMessages.groups).toLowerCase(),
                    placeholder: placeholder,
                }}
                isError={isError}
                isLoading={isLoading}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                    newEntry: manageEntityPermission ? CreateEntityGroupFormModal : undefined,
                }}
            />
        </>
    );
};

export default GroupsTable;
